import { Gaveta, cn } from '@baiocchi/ui'
import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { Sidebar } from './sidebar'
import { Topbar } from './topbar'

const CHAVE_COLAPSADA = 'baiocchi:sidebar-colapsada'

/**
 * Shell da aplicação — sidebar fixa + topbar + área de conteúdo.
 *
 * Duas larguras e não três: expandida (16rem) e colapsada em ícones (4,25rem),
 * com a escolha lembrada. No celular a sidebar não colapsa — vira gaveta, porque
 * uma coluna de ícones de 68px numa tela de 360px come 19% da largura útil só
 * para navegação.
 */
export function LayoutApp() {
  const [colapsada, setColapsada] = useState(() => {
    try {
      return localStorage.getItem(CHAVE_COLAPSADA) === '1'
    } catch {
      return false
    }
  })
  const [gavetaAberta, setGavetaAberta] = useState(false)
  const local = useLocation()

  // Navegar fecha a gaveta e devolve o foco ao topo do conteúdo — sem isso, no
  // celular o usuário navega e continua olhando o menu.
  useEffect(() => {
    setGavetaAberta(false)
    document.getElementById('conteudo-principal')?.scrollTo({ top: 0 })
  }, [local.pathname])

  function alternarColapso() {
    setColapsada((valor) => {
      const proximo = !valor
      try {
        localStorage.setItem(CHAVE_COLAPSADA, proximo ? '1' : '0')
      } catch {
        /* preferência vale só nesta sessão */
      }
      return proximo
    })
  }

  return (
    <div className="flex min-h-screen bg-fundo">
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 hidden shrink-0 transition-[width] lg:block',
          colapsada ? 'w-sidebar-colapsada' : 'w-sidebar',
        )}
      >
        <Sidebar colapsada={colapsada} />
      </aside>

      <Gaveta
        aberta={gavetaAberta}
        onFechar={() => setGavetaAberta(false)}
        titulo="Menu"
        largura="sm"
        lado="esquerda"
      >
        <div className="-mx-5 -my-4 h-full">
          <Sidebar colapsada={false} aoNavegar={() => setGavetaAberta(false)} />
        </div>
      </Gaveta>

      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col transition-[padding]',
          colapsada ? 'lg:pl-sidebar-colapsada' : 'lg:pl-sidebar',
        )}
      >
        <Topbar
          aoAlternarSidebar={alternarColapso}
          aoAbrirNoCelular={() => setGavetaAberta(true)}
        />

        <main
          id="conteudo-principal"
          className="min-w-0 flex-1 px-3 py-4 sm:px-5 sm:py-6"
          tabIndex={-1}
        >
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export interface PropriedadesCabecalhoPagina {
  titulo: string
  descricao?: string
  acoes?: React.ReactNode
  /** Botão `Voltar` — presente nas telas de detalhe e formulário do legado. */
  voltarPara?: string
  className?: string
}

/** Cabeçalho de página: título, descrição e ações primárias. */
export function CabecalhoPagina({
  titulo,
  descricao,
  acoes,
  className,
}: PropriedadesCabecalhoPagina) {
  return (
    <div
      className={cn(
        'mb-4 flex flex-wrap items-end justify-between gap-3 sm:mb-5',
        className,
      )}
    >
      <div className="min-w-0">
        <h1 className="truncate text-xl font-semibold tracking-tight text-conteudo sm:text-2xl">
          {titulo}
        </h1>
        {descricao ? (
          <p className="mt-1 text-sm text-conteudo-suave">{descricao}</p>
        ) : null}
      </div>
      {acoes ? <div className="flex flex-wrap items-center gap-2">{acoes}</div> : null}
    </div>
  )
}
